import React, { useMemo, useState } from 'react';
import { cn } from '../lib/utils';
import type { SiteTreeNode } from '../lib/siteTree';
export type { SiteTreeNode } from '../lib/siteTree';

interface TenantTreeProps {
  tree: SiteTreeNode[];
  selectedIds: Set<number>;
  onChange: (nextSelection: Set<number>) => void;
  search?: string;
  disabled?: boolean;
}

type SelectionState = 'checked' | 'indeterminate' | 'unchecked';

function collectSubtreeIds(node: SiteTreeNode): number[] {
  const ids = [node.id];
  for (const child of node.children) {
    ids.push(...collectSubtreeIds(child));
  }
  return ids;
}

function matchesSearch(node: SiteTreeNode, query: string): boolean {
  if (!query) return true;
  const needle = query.toLowerCase();
  if (node.name.toLowerCase().includes(needle)) return true;
  if (String(node.id).includes(needle)) return true;
  return node.children.some((child) => matchesSearch(child, query));
}

function getSelectionState(node: SiteTreeNode, selected: Set<number>): SelectionState {
  const subtree = collectSubtreeIds(node);
  let selectedCount = 0;
  for (const id of subtree) if (selected.has(id)) selectedCount++;

  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === subtree.length) return 'checked';
  return 'indeterminate';
}

interface NodeRowProps {
  node: SiteTreeNode;
  depth: number;
  selected: Set<number>;
  onToggle: (node: SiteTreeNode, nextChecked: boolean) => void;
  search: string;
  forceOpen: boolean;
  disabled: boolean;
}

const NodeRow: React.FC<NodeRowProps> = ({ node, depth, selected, onToggle, search, forceOpen, disabled }) => {
  const [manuallyOpen, setManuallyOpen] = useState<boolean | null>(null);
  const hasChildren = node.children.length > 0;
  const state = getSelectionState(node, selected);
  const open = manuallyOpen ?? (forceOpen || state !== 'unchecked');

  const visible = matchesSearch(node, search);
  if (!visible) return null;

  const indent = { paddingLeft: `${depth * 18 + 4}px` };

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 py-1.5 pr-2 rounded-md text-sm',
          disabled ? 'opacity-60' : 'hover:bg-gray-50'
        )}
        style={indent}
      >
        <button
          type="button"
          onClick={() => setManuallyOpen(open ? false : true)}
          className={cn(
            'w-6 h-6 flex items-center justify-center text-sm font-semibold text-gray-500 shrink-0',
            !hasChildren && 'invisible'
          )}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <span>{open ? '▾' : '▸'}</span>
        </button>
        <label className="flex items-center gap-2 cursor-pointer select-none flex-1 min-w-0">
          <input
            type="checkbox"
            disabled={disabled}
            className="accent-blue-600"
            ref={(el) => {
              if (el) el.indeterminate = state === 'indeterminate';
            }}
            checked={state === 'checked'}
            onChange={(e) => onToggle(node, e.target.checked)}
          />
          <span className="truncate text-gray-800" title={node.name}>
            {node.name}
          </span>
          <span className="text-xs font-mono text-gray-400 shrink-0">#{node.id}</span>
          {hasChildren && (
            <span className="text-xs text-gray-400 shrink-0">
              ({node.descendantIds.length} descendant{node.descendantIds.length === 1 ? '' : 's'})
            </span>
          )}
        </label>
      </div>
      {hasChildren && open && (
        <div>
          {node.children.map((child) => (
            <NodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              onToggle={onToggle}
              search={search}
              forceOpen={forceOpen}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const TenantTree: React.FC<TenantTreeProps> = ({
  tree,
  selectedIds,
  onChange,
  search = '',
  disabled = false,
}) => {
  const totalNodes = useMemo(() => {
    let count = 0;
    const walk = (node: SiteTreeNode) => {
      count += 1;
      for (const child of node.children) walk(child);
    };
    for (const root of tree) walk(root);
    return count;
  }, [tree]);

  const handleToggle = (node: SiteTreeNode, nextChecked: boolean) => {
    if (!nextChecked) {
      onChange(new Set());
      return;
    }

    onChange(new Set(collectSubtreeIds(node)));
  };

  const clearAll = () => onChange(new Set());

  const forceOpen = Boolean(search && search.trim().length > 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {selectedIds.size} of {totalNodes} organization node{totalNodes === 1 ? '' : 's'} selected
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="px-2 py-1 border rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </div>
      <div className="max-h-96 overflow-auto border rounded-lg bg-white py-1">
        {tree.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            No organizations returned by Nova for this account.
          </div>
        ) : (
          tree.map((node) => (
            <NodeRow
              key={node.id}
              node={node}
              depth={0}
              selected={selectedIds}
              onToggle={handleToggle}
              search={search}
              forceOpen={forceOpen}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
};
